/**
 * Realtime connection-state rune store.
 *
 * Exists because the failure it reports is SILENT. `RealtimeClient` reconnects
 * with exponential backoff, so a dropped socket looks exactly like a quiet
 * inbox: no error, no spinner, just messages that stop arriving. An operator
 * keeps triaging a queue that is no longer true and never learns it — worse
 * than a visible error, because they act on stale data with full confidence.
 *
 * App.svelte already invalidates the conversation queries on every `onOpen`,
 * so the data catches up on reconnect. What was missing is telling the
 * operator that, until it does, what they are looking at may be behind.
 *
 * Three states:
 *   online        — socket open. Renders nothing (see ConnectionStatus).
 *   reconnecting   — dropped, the client is retrying. Usually resolves in
 *                    seconds; not worth alarming anyone.
 *   offline        — repeated failures, or the browser reports no network.
 *                    The operator needs to stop trusting the queue.
 *
 * Deliberately NOT shown: a "retrying in Ns" countdown. `RealtimeClient` owns
 * its backoff schedule and does not expose it, and mirroring the formula here
 * would duplicate knowledge that silently drifts the moment the client's
 * timings change. A wrong countdown is worse than none.
 */

export type ConnectionState = 'online' | 'reconnecting' | 'offline';

/**
 * Consecutive failed opens before escalating past "reconnecting". The client's
 * backoff doubles from 1s, so three failures is roughly seven seconds of
 * silence — long enough that this is not a blip, short enough that an operator
 * has not yet acted on much stale data.
 */
const FAILURES_BEFORE_OFFLINE = 3;

class ConnectionStore {
  #state = $state<ConnectionState>('online');
  #failures = 0;
  /** True once the socket has opened at least once, so first connect is quiet. */
  #everOpened = false;

  get state(): ConnectionState {
    return this.#state;
  }

  /** True when the operator should not trust the list to be current. */
  get stale(): boolean {
    return this.#state !== 'online';
  }

  markOpen(): void {
    this.#everOpened = true;
    this.#failures = 0;
    this.#state = 'online';
  }

  markClosed(): void {
    // Before the first successful open the widget is still starting up; the
    // session gate is what the operator is looking at, not a stale inbox.
    if (!this.#everOpened) return;
    this.#failures += 1;
    this.#state = this.#offlineNow() ? 'offline' : 'reconnecting';
  }

  markError(): void {
    this.markClosed();
  }

  #offlineNow(): boolean {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    return this.#failures >= FAILURES_BEFORE_OFFLINE;
  }

  /** Test seam — resets the singleton between specs. */
  __reset(): void {
    this.#state = 'online';
    this.#failures = 0;
    this.#everOpened = false;
  }
}

export const connection = new ConnectionStore();
